import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// --- MUI & Recharts Dependencies ---
// Make sure you have installed these packages in your project:
// npm install @mui/material @emotion/react @emotion/styled recharts
import {
    Box, Container, Grid, Paper, Typography, TextField, Button,
    AppBar, Toolbar, Tabs, Tab, Alert, Chip, CircularProgress, Skeleton, useTheme
} from '@mui/material';
import { createTheme } from '@mui/material/styles';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

// --- DFINITY Agent Dependency ---
// Make sure you have installed this package:
// npm install @dfinity/agent
import { HttpAgent, Actor } from "@dfinity/agent";


// --- Candid Interface & Actor Logic ---
// This is the interface definition for your canister.
const idlFactory = ({ IDL }) => {
    const Result = IDL.Variant({ 'ok': IDL.Null, 'err': IDL.Text });
    const Time = IDL.Int;
    const BalanceResult = IDL.Record({ 'balance': IDL.Nat, 'timestamp': Time });
    const MessageResult = IDL.Record({
        'userName': IDL.Text,
        'message': IDL.Text,
        'timestamp': Time,
    });
    return IDL.Service({
        'addMessageToBuffer': IDL.Func([IDL.Text, IDL.Text], [Result], []),
        'getBalance': IDL.Func([], [BalanceResult], ['query']),
        'getCount': IDL.Func([IDL.Text], [IDL.Nat], ['query']),
        'getDailyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getDecadeBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getMessageBuffer': IDL.Func([], [IDL.Vec(MessageResult)], ['query']),
        'getMonthlyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getWeeklyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'getYearlyBuffer': IDL.Func([], [IDL.Vec(BalanceResult)], ['query']),
        'initTimer': IDL.Func([], [], []),
        'isTimerRunning': IDL.Func([], [IDL.Bool], ['query']),
    });
};

// --- Main Application Component ---
export default function CanisterDashboard({
    withHeader = true,
    height = 400,
    isTransparent = false
}) {
    const { t } = useTranslation();
    const theme = useTheme();

    // Convert height to proper CSS value
    const getHeightValue = () => {
        if (typeof height === 'string') {
            return height; // Already a CSS string like '50vh', '400px', etc.
        }
        return `${ height }px`; // Convert number to pixels
    };

    // --- State Management ---
    const [actor, setActor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Canister data states
    const [balance, setBalance] = useState(null);
    const [timerRunning, setTimerRunning] = useState(false);
    const [historicalData, setHistoricalData] = useState({ type: 'Daily', data: [] });

    // --- Actor Initialization ---
    useEffect(() => {
        const canisterId = "ojraw-hyaaa-aaaak-ae53q-cai";
        const host = "https://icp-api.io";
        const agent = new HttpAgent({ host });

        // In a local development environment against a local replica, you might need to fetch the root key.
        // For mainnet, this is not required.
        // agent.fetchRootKey().catch(err => {
        //   console.warn("Unable to fetch root key. Check to ensure that your local replica is running");
        //   console.error(err);
        // });

        const canisterActor = Actor.createActor(idlFactory, { agent, canisterId });
        setActor(canisterActor);
    }, []);

    // --- Data Fetching ---
    const fetchAllData = useCallback(async () => {
        if (!actor) return;
        setLoading(true);
        setError('');
        try {
            const [msgs, bal, timerStatus, initialHistory] = await Promise.all([
                actor.getMessageBuffer(),
                actor.getBalance(),
                actor.isTimerRunning(),
                actor.getDailyBuffer(), // Fetch daily by default
            ]);

            setBalance(bal);
            setTimerRunning(timerStatus);
            setHistoricalData({ type: 'Daily', data: initialHistory });
        } catch (e) {
            console.error(e);
            setError(t('analytics.failedToFetchInitialData', { message: e.message }));
        } finally {
            setLoading(false);
        }
    }, [actor]);

    useEffect(() => {
        if (actor) {
            fetchAllData();
        }
    }, [actor, fetchAllData]);


    const handleFetchHistorical = async (bufferType) => {
        if (!actor) return;
        setHistoricalData({ type: bufferType, data: [] }); // Clear old data while loading
        try {
            let data;
            switch (bufferType) {
                case 'Daily': data = await actor.getDailyBuffer(); break;
                case 'Weekly': data = await actor.getWeeklyBuffer(); break;
                case 'Monthly': data = await actor.getMonthlyBuffer(); break;
                case 'Yearly': data = await actor.getYearlyBuffer(); break;
                case 'Decade': data = await actor.getDecadeBuffer(); break;
                default: data = [];
            }
            setHistoricalData({ type: bufferType, data });
        } catch (e) {
            setError(t('analytics.failedToFetchBufferData', { bufferType }));
        }
    };

    // --- Chart Data Preparation ---
    const chartData = historicalData.data.map(item => ({
        timestamp: Number(item.timestamp / BigInt(1000000)), // Use raw ms timestamp
        balance: Number(item.balance) / 1e8
    }));

    // Dynamically calculate the interval to avoid cluttered X-axis labels.
    // This aims for a maximum of around 12 ticks.
    const tickInterval = chartData.length > 12 ? Math.ceil(chartData.length / 12) : 0;


    // --- Chart Formatting Functions ---
    const formatXAxisTick = (unixTime) => {
        const date = new Date(unixTime);
        const viewType = historicalData.type;

        switch (viewType) {
            case 'Daily':
                return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            case 'Weekly':
            case 'Monthly':
                return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
            case 'Yearly':
                return date.toLocaleDateString([], { month: 'short', year: '2-digit' });
            case 'Decade':
                return date.getFullYear().toString();
            default:
                return date.toLocaleDateString();
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const date = new Date(label);
            const formattedDate = date.toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
            });
            const balance = payload[0].value.toLocaleString(undefined, {
                maximumFractionDigits: 4,
            });

            return (
                <Paper sx={{ p: 1.5, backgroundColor: 'rgba(30, 30, 40, 0.9)', border: '1px solid #555' }}>
                    <Typography variant="caption" display="block">{formattedDate}</Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>{`Balance: ${ balance } BTC`}</Typography>
                </Paper>
            );
        }
        return null;
    };

    // --- Render Logic ---
    if (loading) {
        return (
            <Box sx={{ background: theme.palette.background.default, py: 4 }}>
                <Container maxWidth="xl">
                    {
                        withHeader && (
                            <>
                                <Typography variant="h4" gutterBottom>{t('analytics.platformStatistics')}</Typography>
                                <Typography color="text.secondary" sx={{ mb: 4 }}>
                                    {t('analytics.liveMetricsDescription')}
                                </Typography>
                            </>
                        )
                    }
                    <Paper sx={{ p: { xs: 1.5, md: 3 }, height: 480, display: 'flex', flexDirection: 'column' }}>
                        <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
                        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ flex: 1, borderRadius: 1 }} />
                    </Paper>

                </Container>
            </Box>
        );
    }

    return (
        // <Box sx={{ backgroundColor: 'background.default', color: 'text.primary', p: 3 }}>
        <Container maxWidth="xl" sx={{
            height: getHeightValue(),
            // backgroundColor: isTransparent ? 'transparent' : "background.paper"
        }} >
            {
                withHeader && (
                    <>
                        <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>{t('analytics.platformStatistics')}</Typography>
                        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                            {t('analytics.liveMetricsDescription')}
                        </Typography></>
                )
            }

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={3} sx={{
                height: withHeader ? 'calc(100% - 80px)' : '100%',
                // backgroundColor: isTransparent ? 'transparent' : "background.paper"
            }}>

                <Grid item xs={12} sx={{ height: '100%' }}>
                    <Paper sx={{
                        p: 2,
                        backgroundColor: isTransparent ? 'transparent' : "background.paper",
                        backdropFilter: 'blur(10px)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Typography
                            variant={!withHeader ? "subtitle1" : "h6"}
                            gutterBottom
                            sx={{ mb: !withHeader ? 1 : 2 }}
                        >
                            {t('analytics.odinCurrentBalance')}: <span style={{ color: theme.palette.primary.main }}>{balance ? (Number(balance.balance) / 1e8).toLocaleString(undefined, { maximumFractionDigits: 8 }) : <CircularProgress size={withHeader ? 20 : 24} />}{" "} BTC</span>
                        </Typography>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: !withHeader ? 1 : 2 }}>
                            <Tabs
                                value={historicalData.type}
                                onChange={(e, newValue) => handleFetchHistorical(newValue)}
                                aria-label="historical data timeframes"
                                sx={{
                                    minHeight: 'auto',
                                    '& .MuiTab-root': !withHeader ? {
                                        minHeight: 'auto',
                                        py: 0.5,
                                        px: 1,
                                        fontSize: '0.75rem',
                                        minWidth: 'auto',
                                        '&:not(:last-child)': {
                                            marginRight: 0.5
                                        }
                                    } : {
                                        minHeight: 'auto',
                                        py: 1,
                                        fontSize: '0.875rem'
                                    },
                                    '& .MuiTabs-indicator': {}
                                }}
                            >
                                {['Daily', 'Weekly', 'Monthly', 'Yearly', 'Decade'].map(label => <Tab key={label} label={t(`analytics.${ label.toLowerCase() }`)} value={label} />)}
                            </Tabs>
                        </Box>
                        <Box sx={{
                            flex: 1,
                            minHeight: 0,
                            height: !withHeader ? 'calc(100% - 100px)' : 'calc(100% - 80px)'
                        }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={chartData}
                                    margin={!withHeader ?
                                        { top: 5, right: 20, left: 15, bottom: 30 } :
                                        { top: 5, right: 30, left: 20, bottom: 40 }
                                    }
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                    <XAxis
                                        dataKey="timestamp"
                                        stroke="#888"
                                        tickFormatter={formatXAxisTick}
                                        angle={-45}
                                        textAnchor="end"
                                        height={!withHeader ? 45 : 60}
                                        interval={tickInterval}
                                        tick={!withHeader ? { fontSize: 11 } : { fontSize: 12 }}
                                    />
                                    <YAxis
                                        stroke="#888"
                                        domain={['dataMin', 'auto']}
                                        tickFormatter={(tick) => tick.toFixed(2)}
                                        tick={!withHeader ? { fontSize: 11 } : { fontSize: 12 }}
                                        width={!withHeader ? 60 : 70}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        verticalAlign="top"
                                        wrapperStyle={!withHeader ?
                                            { top: -5, fontSize: '12px' } :
                                            { top: -5, fontSize: '14px' }
                                        }
                                    />
                                    <Line type="monotone" dataKey="balance" name="Balance (BTC)"
                                        stroke={theme.palette.primary.main} strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                        {
                            withHeader && (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <a href="https://pe7ey-iqaaa-aaaak-ae54a-cai.icp0.io/" target="_blank" rel="noopener noreferrer">
                                            <Button variant="text" sx={{
                                                color: 'grey.300',
                                                fontSize: '10px',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                    textDecoration: 'underline',
                                                }
                                            }}>Credit to OdinFlow</Button>
                                        </a>
                                    </Box>
                                </>
                            )

                        }

                    </Paper>
                </Grid>

            </Grid>
        </Container>
    );
}
